import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'

import {
  apiManager,
  constants,
  isSupportedBrowser,
  openErrorNotificationWithMsg,
  openNotificationWithMsg,
} from 'helpers'
import config from 'config'
import { useBreakpoints } from 'hooks'
import { ANIMATION } from 'helpers/constants'
import { copyLinkToClipboard } from 'helpers/utils'
import { backdropFilter } from 'helpers/browser'

import AppleAltIcon from 'components/AppleAltIcon'
import CloseIcon from 'components/CloseIcon'
import DownloadIcon from 'components/DownloadIcon'
import PaperClipIcon from 'components/PaperClipIcon'
import SendIcon from 'components/SendIcon'
import Spinner from 'components/Spinner'
import TeleportPrimaryIcon from 'components/TeleportPrimaryIcon'

import chromeLogo from 'assets/images/chrome-2.png'

const {
  ROUTES: { LOGIN },
} = constants

const transition = {
  hidden: { y: '100%' },
  visible: { y: 0 },
}

const HIDDEN_CLASS = 'overflow-hidden'
const MAGIC_TOKEN_DURATION = 240

const Text = ({ children }) => <span className="text-sm text-white opacity-50">{children}</span>

const DeviceContainer = ({ children, className }) => (
  <div className={classnames('relative p-4 w-full rounded-md overflow-hidden', className)}>
    <div
      className="absolute w-full h-full inset-0 opacity-30"
      style={{ backgroundColor: '#31363C' }}
    />
    <div className="relative w-full flex justify-between items-start flex-wrap md:flex-nowrap">
      {children}
    </div>
  </div>
)

const DarkIconButton = ({ Icon, iconText, ...rest }) => (
  <button
    className="bg-midnightBlack w-full flex items-center justify-center rounded-lg px-2 py-4"
    {...rest}
  >
    <Icon className="w-5 h-5 text-white opacity-60 mr-3" />
    <div className="text-sm">{iconText}</div>
  </button>
)

const IconButton = ({ Icon, primary, className, children, ...rest }) => (
  <button
    className={classnames('bg-transparent flex items-center justify-end text-white', className)}
    {...rest}
  >
    <div className={classnames('italic text-sm mr-2.5', { 'text-azureBlue': primary })}>
      {children}
    </div>
    <div
      className={classnames('flex justify-center items-center w-6 h-6 rounded-full', {
        'bg-white bg-opacity-30': !primary,
        'bg-azureBlue': primary,
      })}
    >
      <Icon className="w-3.5 h-3.5" />
    </div>
  </button>
)

const BrowserCompatibilityModal = ({ show, onClose }) => {
  const { md } = useBreakpoints()
  const [isOpen, setIsOpen] = useState(!isSupportedBrowser)
  // For test purpose, do not show modal on staging
  const isModalOpen = useMemo(
    () => (config.isDev ? false : typeof show === 'boolean' ? show : isOpen),
    [show, isOpen]
  )
  const [magicLink, setMagicLink] = useState('')
  const [counter, setCounter] = useState(0)
  const [isSendingLink, setIsSendingLink] = useState(false)

  const handleClose = () => {
    setMagicLink('')
    setIsOpen(false)
    onClose()
  }

  const handleCopy = () => {
    if (counter === 0) {
      openErrorNotificationWithMsg(
        'Link expired to protect your information. Please refresh the page for a new link'
      )
      return
    }

    copyLinkToClipboard(magicLink)
  }

  useEffect(() => {
    if (isModalOpen) {
      document.documentElement.classList.add(HIDDEN_CLASS)
    } else {
      document.documentElement.classList.remove(HIDDEN_CLASS)
    }

    return () => document.documentElement.classList.remove(HIDDEN_CLASS)
  }, [isModalOpen])

  useEffect(() => {
    if (!isModalOpen) {
      return
    }

    const fetchMagicToken = async () => {
      try {
        const { magicToken } = await apiManager.getMagicToken()
        const url = `${window.location.origin}${LOGIN}?magicToken=${magicToken}`
        setMagicLink(url)
        setCounter(MAGIC_TOKEN_DURATION)
      } catch (err) {
        openErrorNotificationWithMsg('There was an error, try again later')
        console.error(err)
      }
    }
    fetchMagicToken()
  }, [isModalOpen, setMagicLink])

  useEffect(() => {
    const timer = counter > 0 && setInterval(() => setCounter(counter - 1), 1000)
    return () => clearInterval(timer)
  }, [counter])

  const handleSendLink = async medium => {
    setIsSendingLink(true)
    const mediumFn = medium === 'text' ? apiManager.textIOSLink : apiManager.emailInstantSigninLink

    try {
      await mediumFn()
      openNotificationWithMsg('success', 'Link sent successfully')
    } finally {
      setIsSendingLink(false)
    }
  }

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.article
          className="fixed z-1000 top-0 left-0 h-screen w-screen text-white bg-opacity-90"
          variants={transition}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={ANIMATION.SOFT_SPRINT}
        >
          <button
            className="absolute z-20 top-5 right-5 rounded-full w-7 h-7 bg-white bg-opacity-30 flex justify-center items-center"
            onClick={handleClose}
            style={backdropFilter('blur(18px)')}
          >
            <CloseIcon className="w-2.5 h-2.5" />
          </button>
          <div className="relative z-10 h-full w-full flex flex-col justify-center px-4 max-w-2xl mx-auto md:px-8">
            <div className="relative flex justify-center items-center p-8 rounded-lg overflow-hidden bg-azureBlue md:p-12 md:mb-2">
              <TeleportPrimaryIcon className="w-20 h-20" />
              <div
                className="w-full h-full absolute"
                style={{
                  background:
                    'linear-gradient(103.65deg, #FF9494 -24.9%, rgba(255, 148, 148, 0) 45.17%, #FF9494 116.73%)',
                }}
              />
            </div>
            <div className="bg-midnightBlack rounded-lg p-4 flex flex-col items-center md:p-10">
              <div className="mb-7 font-bold text-lg text-center max-w-xs pt-4 md:font-ownersWide md:mb-12 md:text-3xl md:font-normal md:pt-0">
                You’re not on a compatible device
              </div>
              <div className="w-full text-center mb-2.5 font-bold md:text-left">
                <Text>WHAT TO USE</Text>
              </div>
              {!md && (
                <DeviceContainer className="mb-2">
                  <div className="flex items-center mb-6">
                    <TeleportPrimaryIcon className="w-10 mr-4" />
                    <Text>Teleport iOS App</Text>
                  </div>
                  <DarkIconButton
                    Icon={AppleAltIcon}
                    iconText="Download the App"
                    onClick={() => window.open(config.ios.appLink, '_blank')}
                  />
                </DeviceContainer>
              )}
              <DeviceContainer className="mb-2">
                <div className="flex items-center mb-6">
                  <img className="w-10 mr-2 md:mr-0.5" src={chromeLogo} alt="chrome" />
                  <Text>Chrome on Desktop</Text>
                </div>
                {md ? (
                  <div className="flex flex-col">
                    <IconButton
                      Icon={PaperClipIcon}
                      primary={true}
                      className="mb-5"
                      onClick={handleCopy}
                    >
                      Copy event URL
                    </IconButton>
                    <IconButton
                      Icon={DownloadIcon}
                      onClick={() => window.open(config.chromeDownloadLink, '_blank')}
                    >
                      Download Chrome
                    </IconButton>
                  </div>
                ) : (
                  <DarkIconButton
                    Icon={SendIcon}
                    iconText="Email Me Event URL"
                    onClick={() => handleSendLink('email')}
                  />
                )}
              </DeviceContainer>
              {md && (
                <DeviceContainer>
                  <div className="flex items-center">
                    <AppleAltIcon className="w-6 h-6 mr-3 ml-1.5 opacity-60" />
                    <Text>iOS Mobile App</Text>
                  </div>
                  <div className="flex flex-col">
                    <IconButton
                      Icon={SendIcon}
                      primary={true}
                      onClick={() => handleSendLink('text')}
                    >
                      {isSendingLink ? <Spinner /> : 'Text me download link'}
                    </IconButton>
                  </div>
                </DeviceContainer>
              )}
            </div>
          </div>
          <div className="absolute z-1 h-full w-full top-0 left-0 bg-black opacity-80" />
        </motion.article>
      )}
    </AnimatePresence>
  )
}

BrowserCompatibilityModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
}

BrowserCompatibilityModal.defaultProps = {
  onClose: () => {},
}

export default BrowserCompatibilityModal
